import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { get } from 'lodash';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

import { Campaign } from 'ideta-library/lib/common/aicm';
import { ProductMeta } from 'ideta-library/lib/common/bot';
import { PageConnection } from 'ideta-library/lib/common/channels/meta';
import { BotRole, User } from 'ideta-library/lib/common/user';
import { AvailableLang } from 'ideta-library/lib/utils/utils';

import { AuthService, LogoutOptions } from '../../services/auth/auth.service';
import { DisplayPageConnection } from '../auto-reply/auto-reply.service';
import { FeedbackService } from '../feedback/feedback.service';
import { MetaService } from '../meta/meta.service';
import { ProtocolDroidService } from './../../protocol-droid/services/protocol-droid.service';
import { CoreSessionService } from './core-session.service';

import { Auth } from '../auth/auth.types';
import { SessionModel } from './session.model';

@Injectable({
  providedIn: 'root'
})
export class UserSessionService implements SessionModel, OnDestroy {
  public fireMeta: Auth;

  private _fbPagesSubject$: BehaviorSubject<PageConnection[]>;
  private _instagramPagesSubject$: BehaviorSubject<PageConnection[]>;
  private _licenceSubject$: BehaviorSubject<any>;
  private _campaignsSubject$: BehaviorSubject<Campaign[]>;

  private _subject$: BehaviorSubject<User>;
  private userSub: Subscription;
  private authSub: Subscription;
  private contentSubs: Subscription[];

  constructor(
    private authService: AuthService,
    private db: AngularFireDatabase,
    private protocolDroidService: ProtocolDroidService,
    private feedbackService: FeedbackService,
    private metaService: MetaService,
    private _session: CoreSessionService
  ) {
    this._fbPagesSubject$ = new BehaviorSubject(null);
    this._instagramPagesSubject$ = new BehaviorSubject(null);
    this._licenceSubject$ = new BehaviorSubject(null);
    this._campaignsSubject$ = new BehaviorSubject(null);
    this._subject$ = new BehaviorSubject(null);
    this.contentSubs = [];
    this.authSub = this.authService
      .getAuth()
      .pipe(
        map(user => user && user.uid),
        distinctUntilChanged()
      )
      .subscribe(userId => {
        if (userId) this.startSession(userId);
      });
  }

  get subject$() {
    return this._subject$.pipe(filter(value => value && !!value.id));
  }

  get distinctSubject$() {
    return this.subject$.pipe(distinctUntilChanged((prev, curr) => prev.id === curr.id));
  }

  get fbPagesSubject$() {
    return this._fbPagesSubject$.pipe(filter(list => !!list));
  }

  get instagramPagesSubject$() {
    return this._instagramPagesSubject$.pipe(filter(list => !!list));
  }

  get licenceSubject$() {
    return this._licenceSubject$.pipe(filter(item => !!item));
  }

  get campaignsSubject$() {
    return this._campaignsSubject$.pipe(filter(list => !!list));
  }

  get id(): string {
    return this.value.id;
  }

  get facebook() {
    return this.value.facebook;
  }

  get instagram() {
    return this.value.instagram;
  }

  get exists(): boolean {
    return !!this._subject$.value;
  }

  public get value(): Partial<User> {
    return this._subject$.value || {};
  }

  startSession(userId: string) {
    // ###M
    this.metaService.disconnectFromFb();
    this.userSub = this.getUser(userId)
      .pipe(
        catchError(error => {
          // If a permission_denied have been triggered, it means that the user
          // have lost his connection to his account (logout from another tab, network error)
          if (error && error.code === 'PERMISSION_DENIED') this.disconnect({ useRedir: true, bypassSessionEnd: true });
          return of(null);
        }),
        filter(user => !!user),
        tap(user => {
          this._subject$.next(user);
          this.protocolDroidService.setLang(this.user.lang || (get(navigator, 'language') as AvailableLang) || get(navigator, 'userLanguage'));
        }),
        switchMap(() => this.distinctSubject$)
      )
      .subscribe(() => {
        this.removeContentSubs();
        this.contentSubs.push(
          this.getConnectedPages('facebook').subscribe(pages => this._fbPagesSubject$.next(pages || [])),
          this.getConnectedPages('instagram').subscribe(pages => this._instagramPagesSubject$.next(pages || [])),
          this.getUserLicence().subscribe(licence => this._licenceSubject$.next(licence || {})),
          this.getCampaigns().subscribe(campaigns => this._campaignsSubject$.next(campaigns || []))
        );
      });
  }

  endSession() {
    // ###M
    // console.log('reset _user');
    this.removeContentSubs();
    this.userSub?.unsubscribe();
    this._subject$.next(null);
  }

  ngOnDestroy() {
    this.endSession();
    this.authSub?.unsubscribe();
  }

  async disconnect(options: LogoutOptions = {}) {
    this.feedbackService.showSpinner();
    if (!options?.bypassSessionEnd) await this.metaService.disconnectFromFb();
    this._session.endSession();
    this.feedbackService.hideSpinner();
    this.endSession();

    // Waiting for all session services to stop;
    return new Promise<void>(resolve => {
      setTimeout(() => this.authService.logout(options).then(() => resolve()), 50);
    });
  }

  get hasContributorRightInBot(): User['hasContributorRightInBot'] {
    return this.user?.hasContributorRightInBot?.bind(this.user) || (() => false);
  }

  refreshPages(channel: ProductMeta) {
    if (channel === 'instagram') this._instagramPagesSubject$.next(this._instagramPagesSubject$.value);
    else this._fbPagesSubject$.next(this._fbPagesSubject$.value);
  }

  private removeContentSubs() {
    this.contentSubs.forEach(sub => sub.unsubscribe());
    this.contentSubs = [];
  }

  private getUser(userId: string): Observable<User> {
    return this.db
      .object<User>('/users/' + userId)
      .valueChanges()
      .pipe(map((user: Partial<User>) => (user ? new User({ ...user, id: userId }) : null)));
  }

  private getConnectedPages(channel: ProductMeta): Observable<DisplayPageConnection[]> {
    const location: string = channel === 'instagram' ? '-instagram' : '';
    return this.db
      .list<PageConnection>(`/auto-replies-connect${location}/${this.id}`)
      .valueChanges()
      .pipe(map(pages => pages.map(page => new PageConnection(page)).filter(page => !page.isDisconnected)));
  }

  private getUserLicence(): Observable<any> {
    return this.db.object(`/auto-replies-credits/${this.id}`).valueChanges();
  }

  private getCampaigns(): Observable<Campaign[]> {
    return this.db
      .list<Campaign>(`/auto-replies/${this.id}`)
      .valueChanges()
      .pipe(map(campaigns => (campaigns || []).map(campaign => new Campaign(campaign))));
  }

  // NOT VALIDATED ------------------------------------------------------------------------

  get email() {
    return this.user.email;
  }

  get lang() {
    return this.user.lang;
  }

  get settings() {
    return this.user.settings;
  }

  get isSuperAdmin() {
    return this.user.status === 'super_admin';
  }

  get isManager() {
    return this.user.isManager;
  }

  get customerId() {
    return this.user.customer && this.user.customer.id;
  }

  get bots(): Record<string, BotRole[]> {
    return this.user.bots;
  }

  private get user(): Partial<User> {
    return this._subject$.value || {};
  }
}
